/**
 * Autosave adapter for notification prefs (Methods + Specifics).
 * Maps UI paths to `saveNotificationPreferences` patches per
 * notification-prefs-granularity.md / gfactor NotificationSettingInput.
 */

import type {
  Last24hStatusKey,
  NotificationMethods,
  NotificationRole,
  NotificationSpecifics,
  StatusChangesKey,
  UserNotificationSettingsV2,
} from './notificationPrefs';

export type NotificationSettingPatchInput = {
  type?: string;
  email?: boolean;
  sms?: boolean;
  reminderTiming?: string;
  reminderRole?: string;
  reminderEnabled?: boolean;
  summaryWindow?: string;
  summaryRole?: string;
  summaryEnabled?: boolean;
  summarySendTime?: string;
  sendTimeZone?: string;
  last24Status?: string;
  last24StatusRole?: string;
  last24StatusEnabled?: boolean;
  statusChange?: string;
  statusRole?: string;
  statusEnabled?: boolean;
};

export type SaveNotificationPreferencesFn = (vars: {
  input: NotificationSettingPatchInput;
}) => Promise<unknown>;

export type NotificationPrefsSaveResult =
  | { status: 'saved'; kind: 'methods-channel' | 'specifics' }
  | { status: 'skipped'; kind: 'unknown-path'; path: string };

const REMINDER_TIMING_TO_API: Record<
  keyof NotificationSpecifics['jobReminders'],
  string
> = {
  twentyFourHoursBefore: '24h_before',
  oneHourBefore: '1h_before',
  oneHourAfter: '1h_after',
  twentyFourHoursAfter: '24h_after',
};

const SUMMARY_WINDOW_TO_API: Record<'next24Hours' | 'last24Hours', string> = {
  next24Hours: 'next_24_hours',
  last24Hours: 'last_24_hours',
};

const REMINDER_TIMING_KEYS = Object.keys(REMINDER_TIMING_TO_API) as Array<
  keyof NotificationSpecifics['jobReminders']
>;

const ROLES: NotificationRole[] = ['assignee', 'owner', 'include'];

function isRole(value: string): value is NotificationRole {
  return (ROLES as string[]).includes(value);
}

function withSendTimeZone(
  input: NotificationSettingPatchInput,
  value: UserNotificationSettingsV2
): NotificationSettingPatchInput {
  const tz = value.specifics.jobSummaries.sendTimeZone;
  if (!tz) return input;
  return { ...input, sendTimeZone: tz };
}

export async function saveNotificationPrefsChange(args: {
  path: string;
  value: UserNotificationSettingsV2;
  save: SaveNotificationPreferencesFn;
}): Promise<NotificationPrefsSaveResult> {
  const { path, value, save } = args;
  const input = buildPatchInput(path, value);
  if (!input) {
    return { status: 'skipped', kind: 'unknown-path', path };
  }

  await save({ input });
  return {
    status: 'saved',
    kind: path.startsWith('methods.') ? 'methods-channel' : 'specifics',
  };
}

export function buildPatchInput(
  path: string,
  value: UserNotificationSettingsV2
): NotificationSettingPatchInput | null {
  if (path.startsWith('methods.messages.')) {
    return {
      type: 'messages',
      email: value.methods.messages.email,
      sms: value.methods.messages.sms,
      sendTimeZone: value.specifics.jobSummaries.sendTimeZone,
    };
  }

  if (path.startsWith('methods.jobReminders.')) {
    return {
      type: 'job_reminders',
      email: value.methods.jobReminders.email,
      sms: value.methods.jobReminders.sms,
      sendTimeZone: value.specifics.jobSummaries.sendTimeZone,
    };
  }

  if (path.startsWith('methods.jobSummaries.')) {
    return {
      type: 'job_summaries',
      email: value.methods.jobSummaries.email,
      sms: false,
      sendTimeZone: value.specifics.jobSummaries.sendTimeZone,
    };
  }

  if (path === 'methods.statusChanges.email') {
    return {
      type: 'status_changes',
      email: value.methods.statusChanges.email,
      sms: false,
      sendTimeZone: value.specifics.jobSummaries.sendTimeZone,
    };
  }

  const reminderMatch = path.match(
    /^specifics\.jobReminders\.([^.]+)\.([^.]+)$/
  );
  if (reminderMatch) {
    const timing =
      reminderMatch[1] as keyof NotificationSpecifics['jobReminders'];
    const role = reminderMatch[2];
    if (REMINDER_TIMING_KEYS.includes(timing) && isRole(role)) {
      return withSendTimeZone(
        {
          reminderTiming: REMINDER_TIMING_TO_API[timing],
          reminderRole: role,
          reminderEnabled: Boolean(value.specifics.jobReminders[timing][role]),
        },
        value
      );
    }
  }

  const summaryRoleMatch = path.match(
    /^specifics\.jobSummaries\.(next24Hours|last24Hours)\.(assignee|owner|include)$/
  );
  if (summaryRoleMatch) {
    const window = summaryRoleMatch[1] as 'next24Hours' | 'last24Hours';
    const role = summaryRoleMatch[2] as NotificationRole;
    return withSendTimeZone(
      {
        summaryWindow: SUMMARY_WINDOW_TO_API[window],
        summaryRole: role,
        summaryEnabled: Boolean(value.specifics.jobSummaries[window][role]),
      },
      value
    );
  }

  const summarySendTimeMatch = path.match(
    /^specifics\.jobSummaries\.(next24Hours|last24Hours)\.sendTime$/
  );
  if (summarySendTimeMatch) {
    const window = summarySendTimeMatch[1] as 'next24Hours' | 'last24Hours';
    return withSendTimeZone(
      {
        summaryWindow: SUMMARY_WINDOW_TO_API[window],
        summarySendTime: value.specifics.jobSummaries[window].sendTime,
      },
      value
    );
  }

  const last24Match = path.match(
    /^specifics\.jobSummaries\.last24Hours\.statusChangesToInclude\.([^.]+)\.([^.]+)$/
  );
  if (last24Match) {
    const status = last24Match[1] as Last24hStatusKey;
    const role = last24Match[2];
    if (isRole(role)) {
      return withSendTimeZone(
        {
          last24Status: status,
          last24StatusRole: role,
          last24StatusEnabled: Boolean(
            value.specifics.jobSummaries.last24Hours.statusChangesToInclude[
              status
            ]?.[role]
          ),
        },
        value
      );
    }
  }

  const statusMatch = path.match(
    /^specifics\.statusChanges\.([^.]+)\.([^.]+)$/
  );
  if (statusMatch) {
    const status = statusMatch[1] as StatusChangesKey;
    const role = statusMatch[2];
    if (isRole(role)) {
      return withSendTimeZone(
        {
          statusChange: status,
          statusRole: role,
          statusEnabled: Boolean(value.specifics.statusChanges[status]?.[role]),
        },
        value
      );
    }
  }

  return null;
}

/** Build a Specifics status-cell path for tests / patch API. */
export function statusSpecificsPath(
  status: StatusChangesKey,
  role: NotificationRole
): string {
  return `specifics.statusChanges.${status}.${role}`;
}

export type MethodsChannelPath =
  | 'methods.messages.email'
  | 'methods.messages.sms'
  | 'methods.jobReminders.email'
  | 'methods.jobReminders.sms'
  | 'methods.jobSummaries.email'
  | 'methods.statusChanges.email';

export function readMethodsChannel(
  methods: NotificationMethods,
  path: MethodsChannelPath
): boolean {
  const parts = path.split('.') as [
    'methods',
    keyof NotificationMethods,
    'email' | 'sms'
  ];
  const group = methods[parts[1]] as { email?: boolean; sms?: boolean };
  return Boolean(group[parts[2]]);
}

export function readReminderRole(
  specifics: NotificationSpecifics,
  timing: keyof NotificationSpecifics['jobReminders'],
  role: NotificationRole
): boolean {
  return Boolean(specifics.jobReminders[timing][role]);
}
